import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon } from 'components/icons';
import cx from 'classnames';
import { useAppDispatch } from 'store/hooks';
import { setRewardsOpen, setRewardsTab } from 'store/uiSlice';
import { fetchMyLevel } from 'store/authSlice';
import { fetchLevels } from 'store/levelSlice';
import Skeleton from '../skeleton/Skeleton';
import styles from './spinDetails.module.scss';

interface LevelUpDetailsProps {
  details: any;
  onBack: () => void;
  onPlayClick?: () => void;
  isLoading?: boolean;
  title?: string | null;
}

const LevelUpDetails: React.FC<LevelUpDetailsProps> = ({
  details,
  title,
  onBack,
  onPlayClick,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [isFadingOut, setIsFadingOut] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);
  const dispatch = useAppDispatch();

  const handleBack = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      onBack();
    }, 200);
  };

  const handleOpenLevels = async () => {
    setIsFetching(true);
    await Promise.all([dispatch(fetchMyLevel()), dispatch(fetchLevels())]);
    setIsFetching(false);

    if (onPlayClick) {
      onPlayClick();
    }
    dispatch(setRewardsTab('levelUp'));
    dispatch(setRewardsOpen(true));
  };

  return (
    <div className={cx(styles.container, { [styles.fadingOut]: isFadingOut })}>
      <button className={styles.backButton} onClick={handleBack} aria-label="Go back">
        <ChevronLeftIcon width="20" height="20" />
      </button>

      <div className={styles.header} style={{ padding: '0 40px' }}>
        <h2>{isLoading ? <Skeleton width={180} height={24} /> : title || 'Level up!'}</h2>
        {!isLoading && details?.message && (
          <div style={{ marginTop: '8px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
            {details.message}
          </div>
        )}
      </div>

      <div className={styles.statsGrid} style={{ gridTemplateColumns: '1fr' }}>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={80} height={28} />
          ) : (
            <span className={styles.statValue}>{details?.newTierName || '-'}</span>
          )}
          <span className={styles.statLabel}>
            {t('notifications.labels.newLevel', 'New Level')}
          </span>
        </div>
      </div>

      <div className={styles.cardWrapper} style={{ marginTop: '16px', marginBottom: '16px' }}>
        <button
          className={styles.claimButton}
          onClick={handleOpenLevels}
          disabled={isFetching || isLoading}
        >
          {isFetching
            ? t('notifications.actions.loading', 'Loading...')
            : t('notifications.actions.levels', 'Levels')}
        </button>
      </div>
    </div>
  );
};

export default LevelUpDetails;
